/**
 * Plot frame of the cartesian charts (bar, line): how much room the value
 * axis and the category labels take, and which category labels fit.
 */
import { formatTickLabel, type NxValueAxis } from './axis';

/** Average glyph width of the axis type (font-size-xs) in px. */
export const AXIS_CHAR_PX = 6.8;
/** Height of the category label row under the plot, in px. */
export const X_LABEL_BAND = 22;
/** Headroom above the highest tick, in px (room for a tick label's half height). */
export const PAD_TOP = 10;

export interface NxPlotFrame {
  /** Left edge of the plot (right edge of the tick labels). */
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
  /** Pixel y of a value. */
  y: (value: number) => number;
  tickLabels: { value: number; y: number; text: string }[];
}

/** Plot frame for a value axis in a box of `width`×`height` px. */
export function plotFrame(
  axis: NxValueAxis,
  width: number,
  height: number,
  opts: {
    locale?: string;
    xLabels?: boolean;
    padRight?: number;
    formatTick?: (v: number) => string;
  }
): NxPlotFrame {
  const text = (v: number) =>
    opts.formatTick ? opts.formatTick(v) : formatTickLabel(v, axis.ticks, opts.locale);
  const labels = axis.ticks.map(text);
  const chars = Math.max(1, ...labels.map((l) => l.length));
  const left = Math.ceil(chars * AXIS_CHAR_PX) + 10;
  const right = Math.max(left + 20, width - (opts.padRight ?? 6));
  const top = PAD_TOP;
  const bottom = Math.max(top + 20, height - (opts.xLabels === false ? 6 : X_LABEL_BAND));
  const span = axis.max - axis.min || 1;
  const y = (value: number) => top + ((axis.max - value) / span) * (bottom - top);
  return {
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
    y,
    tickLabels: axis.ticks.map((value, i) => ({ value, y: y(value), text: labels[i]! })),
  };
}

/**
 * Category labels that fit side by side at `step` px apart: every label while
 * they fit, else every 2nd, 3rd … (always keeping the first). Each label is
 * cut to the room it has, with an ellipsis.
 */
export function thinLabels(
  labels: readonly string[],
  step: number
): { index: number; text: string }[] {
  if (!labels.length || step <= 0) return [];
  const longest = Math.max(1, ...labels.map((l) => l.length));
  const minRoom = 4 * AXIS_CHAR_PX; // a label shorter than „Ab…“ says nothing
  // Room a label may ask for before neighbours get skipped: its full length,
  // but at most ~8 glyphs — longer ones are cut rather than thinned out.
  const need = Math.max(minRoom, Math.min(longest * AXIS_CHAR_PX + 8, 8 * AXIS_CHAR_PX + 8));
  let every = 1;
  while (step * every < need && every < labels.length) every++;
  const room = Math.max(1, Math.floor((step * every - 8) / AXIS_CHAR_PX));
  const out: { index: number; text: string }[] = [];
  for (let i = 0; i < labels.length; i += every) {
    const l = labels[i]!;
    out.push({ index: i, text: l.length > room ? `${l.slice(0, Math.max(1, room - 1))}…` : l });
  }
  return out;
}
